import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import {
  Bar, CartesianGrid, ComposedChart, LabelList, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  getDuplicatePaymentWatch, getUnconfirmedPayments,
  getDeliveryTrend, getDeliveryRepeats,
} from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  GREEN, AMBER, RED, NAVY, AVG_INK,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, StatusChip, identityColumns, drillColumn, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel, isSubDayGran,
  fmtInt, fmtDate, fmtAxisDate, fmtTime, safeNum,
} from "./opsShared";

// Two confirmations closer together than this on one conversation is the
// double-charge shape rather than a customer doing two separate things.
const SUSPICIOUS_MINUTES = 30;

export default function DuplicateWatchDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
  }), [range, gran, timeZone]);

  // The row whose thread is open in the popup. One piece of state for all
  // four tables — they all drill to the same place.
  const [drill, setDrill] = useState(null);

  const { data, loading, error } = useOpsData({
    duplicates:    getDuplicatePaymentWatch,
    unconfirmed:   getUnconfirmedPayments,
    deliveryTrend: getDeliveryTrend,
    repeats:       getDeliveryRepeats,
  }, params, tzReady);

  const duplicates  = data.duplicates || [];
  const unconfirmed = data.unconfirmed || [];
  const repeats     = data.repeats || [];
  const suspicious  = duplicates.filter((d) => safeNum(d.minutesApart) <= SUSPICIOUS_MINUTES);
  const paymentRepeats = repeats.filter((r) => safeNum(r.involvedPayment) === 1);
  // Charges only. A promise that was never confirmed is a service failure; a
  // PAYMENT that was never confirmed is money the customer does not know left
  // their account, which is a different order of problem.
  const unconfirmedPaid = unconfirmed.filter((u) => u.eventType === "Payment");
  const chargedTwice = duplicates.reduce((a, d) => a + safeNum(d.totalCharged), 0);

  const delTrend = (data.deliveryTrend || []).map((r) => ({
    date:     fmtAxisDate(r.summary_date, gran, timeZone),
    handled:  safeNum(r.messagesHandled),
    attempts: safeNum(r.deliveryAttemptsPerMessage),
    twice:    safeNum(r.messagesSentTwiceOrMore),
  }));

  const worstAttempts = delTrend.reduce((a, r) => Math.max(a, r.attempts), 0);
  // 1.0001 rather than 1: the value is a decimal(6,3) average and floating
  // comparison against exactly 1 would flag rounding noise as a failure.
  const attemptsStatus = worstAttempts > 1.0001 ? "bad" : "ok";

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  const openThread = (row) => setDrill(row);

  // Duplicates now come from the payment ledger, so the columns describe
  // CHARGES rather than messages: how many, how much, how far apart.
  const duplicateColumns = [
    drillColumn(openThread),
    { key: "localDay", label: "Date", width: 100, render: (r) => fmtDate(r.localDay) },
    ...identityColumns,
    { key: "paymentEvents", label: "Charges", width: 90, align: "right",
      render: (r) => <StatusChip label={String(safeNum(r.paymentEvents))} tone="bad" /> },
    { key: "totalCharged", label: "Total Charged", width: 140, align: "right",
      render: (r) => (
        r.totalCharged == null ? "—" : (
          <>
            ${Number(r.totalCharged).toFixed(2)}
            {Number(r.totalFees) > 0 && (
              <Box component="span" sx={{ fontSize: 10, color: "text.disabled", ml: 0.5 }}>
                (${Number(r.totalFees).toFixed(2)} fees)
              </Box>
            )}
          </>
        )
      ) },
    { key: "minutesApart", label: "Time Apart", width: 110, align: "right",
      render: (r) => {
        const m = safeNum(r.minutesApart);
        return m <= SUSPICIOUS_MINUTES
          ? <StatusChip label={`${m} min`} tone="bad" />
          : `${m} min`;
      } },
    { key: "firstAtUtc", label: "First", width: 160, render: (r) => fmtTime(r.firstAtUtc, timeZone) },
    { key: "lastAtUtc",  label: "Last",  width: 160, render: (r) => fmtTime(r.lastAtUtc, timeZone) },
    { key: "charges", label: "Amounts & Confirmations", width: 300, mono: true },
  ];

  // The silent-money table. lastAgentReply is what makes a row diagnosable
  // without opening it: "(no reply sent)" and the fall-back text are two
  // different stories.
  const unconfirmedColumns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "eventType", label: "Type", width: 100,
      render: (r) => (
        <StatusChip label={r.eventType} tone={r.eventType === "Payment" ? "bad" : "warn"} />
      ) },
    { key: "totalAmount", label: "Charged", width: 130, align: "right",
      // The card was hit for TotalAmount, not PaymentAmount. Fee shown inline
      // when non-zero so the figure reconciles against a PNM statement.
      render: (r) => (
        r.totalAmount == null ? "—" : (
          <>
            ${Number(r.totalAmount).toFixed(2)}
            {Number(r.feeAmount) > 0 && (
              <Box component="span" sx={{ fontSize: 10, color: "text.disabled", ml: 0.5 }}>
                (${Number(r.feeAmount).toFixed(2)} fee)
              </Box>
            )}
          </>
        )
      ) },
    { key: "confirmationNumber", label: "Confirmation", width: 150, mono: true },
    { key: "eventAtUtc", label: "Processed", width: 160,
      render: (r) => fmtTime(r.eventAtUtc, timeZone) },
    { key: "maxDeliveryAttempts", label: "Attempts", width: 85, align: "right",
      render: (r) => {
        const n = safeNum(r.maxDeliveryAttempts);
        return n >= 2 ? <StatusChip label={String(n)} tone="bad" /> : n || "—";
      } },
    { key: "lastAgentReply", label: "Last Thing AI Said", width: 300 },
  ];

  const repeatColumns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "deliveryAttempts", label: "Attempts", width: 90, align: "right",
      render: (r) => <StatusChip label={String(safeNum(r.deliveryAttempts))} tone="bad" /> },
    { key: "involvedPayment", label: "Payment", width: 120, align: "center",
      render: (r) => (
        safeNum(r.involvedPayment) === 1
          ? <StatusChip label="Verify PNM" tone="bad" />
          : <StatusChip label="No" tone="default" />
      ) },
    { key: "customerMessage", label: "Customer's Message", width: 180 },
    { key: "responseSeconds", label: "Response Time", width: 110, align: "right",
      render: (r) => `${safeNum(r.responseSeconds)} sec` },
    { key: "receivedUtc", label: "Received", width: 160, render: (r) => fmtTime(r.receivedUtc, timeZone) },
    { key: "agentReply", label: "AI's Reply", width: 300 },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Checking for duplicate payments…" />}

      {!loading && (
        <>
          <SectionTitle>Payment Safety</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            {/* Every threshold here is zero. There is no acceptable level of
                "we charged a card and did not tell them", so these are
                pass/fail rather than banded. */}
            <CounterCard label="Charged, Never Confirmed" value={fmtInt(unconfirmedPaid.length)}
              color={GREEN} status={unconfirmedPaid.length === 0 ? "ok" : "bad"} big
              sub="Money moved, customer not told" />
            <CounterCard label="Accounts Charged Twice" value={fmtInt(duplicates.length)}
              color={GREEN} status={duplicates.length === 0 ? "ok" : "bad"} big
              sub={chargedTwice > 0 ? `$${chargedTwice.toFixed(2)} across those days` : "Same local day"} />
            <CounterCard label="Charges Minutes Apart" value={fmtInt(suspicious.length)}
              color={GREEN} status={suspicious.length === 0 ? "ok" : "bad"} big
              sub={`Within ${SUSPICIOUS_MINUTES} min`} />
            <CounterCard label="Delivery Attempts per Message"
              value={worstAttempts ? worstAttempts.toFixed(3) : "—"}
              color={GREEN} status={attemptsStatus} big
              sub="Must read 1.000" />
            <CounterCard label="Messages Delivered Twice" value={fmtInt(repeats.length)}
              color={GREEN} status={repeats.length === 0 ? "ok" : "bad"} big
              sub="Reached AI more than once" />
          </Box>

          {worstAttempts > 1.0001 && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
              Delivery attempts per message is above 1.000, so at least one customer message
              reached AI more than once. That is the exact condition that made duplicate
              charges possible. Confirm the worker is running the single-delivery build
              before investigating anything else on this page.
            </Alert>
          )}

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Delivery attempts per message")}
              right="target 1.000" height={250}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={delTrend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  {/* Floored at 1 — the metric cannot go below it, and letting
                      the axis auto-scale to 0.99..1.01 would turn rounding into
                      a mountain range. */}
                  <YAxis yAxisId="left" domain={[1, (max) => Math.max(1.1, max)]}
                    tick={{ fontSize: 10, fill: "#888" }} tickFormatter={(v) => v.toFixed(2)} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: NAVY }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <ReferenceLine yAxisId="left" y={1} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
                  <Bar yAxisId="right" dataKey="handled" name="Messages handled"
                    fill={`${NAVY}22`} radius={[3, 3, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="attempts" name="Attempts per message"
                    stroke={RED} strokeWidth={2} dot={{ r: 3 }}>
                    <LabelList dataKey="attempts" position="top" offset={8} fontSize={10}
                      fill={RED} formatter={(v) => (v > 1.0001 ? v.toFixed(2) : "")} />
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              Every message should reach AI exactly once. Through June–August this ran
              between 2.1 and 2.9, meaning most messages were sent to the AI two or three
              times — each repeat a chance for the same payment to be taken again.
            </Caption>
          </Box>

          {/* ── SILENT MONEY, FIRST ──
              A charge the customer was never told about outranks a possible
              double charge: with a duplicate they at least know something
              happened. */}
          <SectionTitle right={`${unconfirmed.length} transactions`}>
            Processed But Never Confirmed
          </SectionTitle>
          {unconfirmedPaid.length > 0 && (
            <Alert severity="error" sx={{ mb: 1.5, fontSize: 13 }}>
              {unconfirmedPaid.length}{" "}
              {unconfirmedPaid.length === 1 ? "payment was" : "payments were"} processed with a
              confirmation number that appears nowhere in the customer's conversation. They do
              not know the money left their account. Open the row to see what AI last said.
            </Alert>
          )}
          <DetailTable
            columns={unconfirmedColumns}
            rows={unconfirmed}
            emptyText="Every processed transaction was confirmed to the customer."
            maxHeight={400}
          />
          <Caption>
            Read from customerconvevent, then checked against the conversation for the
            confirmation number. A promise here is a service failure; a payment here is money
            the customer has not been told about, and is the first thing to reconcile.
          </Caption>

          <SectionTitle right={`${duplicates.length} accounts`}>
            Accounts Charged More Than Once
          </SectionTitle>
          {suspicious.length > 0 && (
            <Alert severity="error" sx={{ mb: 1.5, fontSize: 13 }}>
              {suspicious.length} {suspicious.length === 1 ? "account was" : "accounts were"}{" "}
              charged more than once within {SUSPICIOUS_MINUTES} minutes. Two charges that close
              together on one conversation are unlikely to be two intentional payments — check
              the PNM before anything else.
            </Alert>
          )}
          <DetailTable
            columns={duplicateColumns}
            rows={duplicates}
            emptyText="No account received more than one payment confirmation in this range."
            maxHeight={400}
          />
          <Caption>
            Counted from CF-AI3-PAY events, not from AI's wording — a reworded confirmation
            message would make text matching go quietly blind. Multiple charges are not
            automatically wrong; Time Apart is the discriminator, and the Amounts column shows
            whether two charges were for the same sum.
          </Caption>

          <SectionTitle right={`${repeats.length} shown`}>
            Messages Delivered More Than Once
          </SectionTitle>
          <DetailTable
            columns={repeatColumns}
            rows={repeats}
            emptyText="None — every message reached AI exactly once."
            maxHeight={400}
          />
          <Caption>
            Each row is a candidate for PNM reconciliation. Rows marked "Verify PNM"
            produced a payment confirmation, so the same charge may also have been attempted
            on an earlier delivery that never reported back.
          </Caption>
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
        subtitle={drill?.confirmationNumber ? `confirmation #${drill.confirmationNumber}` : null}
      />
    </Box>
  );
}